import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import TextInput from "@components/shared/TextInput";
import SelectInput from "@components/shared/SelectInput";
import DropdownWithCheckboxes from "@components/shared/DropdownWithCheckboxes";
import { BASE_URL } from "@utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { getAllQuestions, getAllRoles, getAllSalutations } from "@/store/thunks/authAction";
import { toast } from "react-toastify";
import JSEncrypt from "jsencrypt";
import apiClient from "@/services/api/apiClient";

const RegisterUserTableGAAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [salutations, setSalutations] = useState([]);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const { user, token } = useSelector((state) => state.auth)
  const [genders, setGenders] = useState([
    { text: "MALE", id: 1 },
    { text: "FEMALE", id: 2 },
    { text: "OTHERS", id: 3 },
  ]);
  const [roles, setRoles] = useState([]);
  const [targetLanguages, setTargetLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState("");
  const [showLanguagePopup, setShowLanguagePopup] = useState(false);
  const [userId, setUserId] = useState(0);
  const [usernameError, setUsernameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [publicKey, setPublicKey] = useState(null);



  const getSalutations = async () => {
    try {
      setLoading(true);
      const response = await dispatch(getAllSalutations(navigate));
      setSalutations(response.data || []);
    } catch (error) {
      console.error("Failed to fetch salutations:", error);
      setRegistrationError("Failed to load salutations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getRoles = async () => {
    try {
      setLoading(true);
      const response = await dispatch(getAllRoles(navigate));
      const filteredRoles = (response.data || []).filter(
        (role) =>
          ![
            "SUPERADMIN",
            "GS ADMIN",
            "DEVANAGARI ADMIN",
            "DEVANAGARI USER",
          ].includes(role.name)
      );
      setRoles(filteredRoles);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      setRegistrationError("Failed to load roles. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getSecurityQuestions = async () => {
    try {
      setLoading(true);
      const response = await dispatch(getAllQuestions(navigate));
      setSecurityQuestions(response.data || []);
    } catch (error) {
      console.error("Failed to fetch security questions:", error);
      setRegistrationError("Failed to load security questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getTargetLanguages = async () => {
    try {
      setLoading(true);
      const response = await apiClient(BASE_URL + "api/user/languages", {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`

        }
      },navigate);
      setTargetLanguages(response.data || []);
    } catch (error) {
      console.error("Failed to fetch target languages:", error);
      setRegistrationError("Failed to load target languages. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const checkUsernameExists = async (username) => {
    if (!username) {
      setUsernameError("");
      return;
    }
    try {
      const data = await apiClient(BASE_URL + `api/user/register/username`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ username }),
        credentials: "include",
      },navigate);
      if (data.data) {
        setUsernameError(data.message);
      } else {
        setUsernameError(data.message);
      }
    } catch (error) {
      console.error("Failed to check username:", error);
      setUsernameError("Error checking username availability.");
    }
  };

  const checkEmailExists = async (email) => {
    if (!email) {
      setEmailError("");
      return;
    }
    try {
      const data = await apiClient(BASE_URL + `api/user/register/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ email }),
        credentials: "include",
      },navigate);
      if (data.data) {
        setEmailError(data.message);
      } else {
        setEmailError(data.message);
      }
    } catch (error) {
      console.error("Failed to check username:", error);
      setEmailError("Error checking email availability.");
    }
  };

  const checkMobileExists = async (mobile) => {
    if (!mobile) {
      setMobileError("");
      return;
    }
    try {
      const data = await apiClient(BASE_URL + `api/user/register/mobile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ mobile }),
        credentials: "include",
      },navigate);
      if (data.data) {
        setMobileError(data.message);
      } else {
        setMobileError(data.message);
      }
    } catch (error) {
      console.error("Failed to check username:", error);
      setMobileError("Error checking mobile number availability.");
    }
  };

  // Fetch public key from API
  const fetchKey = async () => {
    try {
      const data = await apiClient(`${BASE_URL}api/user/public-key`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      },navigate);
      console.log("Public Key fetched:", data);
      if (data.data) {
        setPublicKey(data.data);
      } else {
        console.error("Failed to fetch public key");
      }
    } catch (err) {
      console.error("[fetchKey] Error:", err);
    }
  };

  useEffect(() => {
    getSalutations();
    getRoles();
    getSecurityQuestions();
    getTargetLanguages();
    fetchKey();
  }, [dispatch]);

  const validationSchema = Yup.object({
    username: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email format").required("Required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Required"),
    password2: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Required"),

    firstName: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "First name must not contain numbers or special characters")
      .required("Required"),

    middleName: Yup.string()
      .matches(/^[A-Za-z\s]*$/, "Middle name must not contain numbers or special characters").required("Required"),

    lastName: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "Last name must not contain numbers or special characters")
      .required("Required"),

    mobileNo: Yup.string()
      .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
      .required("Required"),

    salutationId: Yup.string().required("Required"),
    gender: Yup.string().required("Required"),
    roleId: Yup.string().required("Required"),
    securityQuestionId: Yup.string().required("Required"),
    recoveryHint: Yup.string().required("Required"),
    targetLanguages: Yup.array()
      .min(1, "Please select at least one language")
      .required("Required"),
  });


  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="flex-1 p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-4xl mx-auto">
          <div className="p-6 border-b border-[#ec7026]">
            <h1 className="text-2xl font-['Semantic/Strong'] text-[#ec7026] text-center">
              GS Solutions User Registration
            </h1>
          </div>
          <div className="p-6">
            {registrationError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 font-['Semantic/Regular']">
                <p>{registrationError}</p>
              </div>
            )}
            {loading && (
              <div className="absolute inset-0 bg-gray-100/50 flex items-center justify-center z-10">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-t-[#ec7026] border-b-[#d85d1a]"></div>
              </div>
            )}
            <Formik
              initialValues={{
                username: "",
                email: "",
                password: "",
                password2: "",
                firstName: "",
                middleName: "",
                lastName: "",
                mobileNo: "",
                salutationId: "",
                gender: "",
                roleId: "",
                securityQuestionId: "",
                recoveryHint: "",
                targetLanguages: [],
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  setLoading(true);
                  setRegistrationError("");

                  let encryptedPassword;
                  if (publicKey) {
                    const encrypt = new JSEncrypt();
                    encrypt.setPublicKey(publicKey);
                    encryptedPassword = encrypt.encrypt(values.password);
                  } else {
                    console.error("Public key not available. Cannot encrypt password.");
                    throw new Error("Encryption error. Please try again later.");
                  }

                  const userData = {
                    username: values.username,
                    email: values.email,
                    password: encryptedPassword,
                    firstName: values.firstName,
                    middleName: values.middleName || "",
                    lastName: values.lastName,
                    mobile: parseInt(values.mobileNo, 10),
                    salutationId: parseInt(values.salutationId, 10),
                    gender: values.gender,
                    roleId: parseInt(values.roleId, 10),
                    securityQuestionId: parseInt(values.securityQuestionId, 10),
                    recoveryHint: values.recoveryHint,
                    ministryId: 1,
                    organizationId: 1,
                    languageIds: values.targetLanguages.map((id) => parseInt(id, 10)),
                  };

                  console.log("Submitting userData:", userData);

                  const data = await apiClient(BASE_URL + "api/gsAdmin/register", {
                    method: "POST",
                    credentials: "include",
                    headers: {
                      "Content-Type": "application/json",

                      "Authorization": `Bearer ${token}`

                    },
                    body: JSON.stringify(userData),
                  },navigate);

                  console.log(data)

                  if (data.success) {
                    setUserId(data.data);
                    setShowLanguagePopup(true);
                    toast.success("Languages saved successfully!", {
                      position: "top-right",
                      autoClose: 3000,
                      hideProgressBar: false,
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                      theme: "colored",
                      style: { background: "#ec7026", color: "#fff" },
                    });
                    navigate("/GACSAdmin/manage-users");
                  } else {
                    setRegistrationError(data.message);
                  }
                } catch (error) {
                  setRegistrationError(error.message);
                } finally {
                  setSubmitting(false);
                  setLoading(false);
                }
              }}
            >
              {({ isSubmitting, errors, touched, values, setFieldValue }) => (
                <Form className="relative grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex flex-col">
                    <TextInput
                      label="Username"
                      name="username"
                      type="text"
                      className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
                      labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                      errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                      onBlur={() => checkUsernameExists(values.username)}
                      error={touched.username && errors.username}
                    />
                    {usernameError && (
                      <span className="text-red-600 font-['Semantic/Strong'] mt-1 text-sm">
                        {usernameError}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <TextInput
                      label="Email"
                      name="email"
                      type="text"
                      className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
                      labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                      errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                      onBlur={() => checkEmailExists(values.email)}
                      error={touched.email && errors.email}
                    />
                    {emailError && (
                      <span className="text-red-600 font-['Semantic/Strong'] mt-1 text-sm">
                        {emailError}
                      </span>
                    )}
                  </div>
                  <TextInput
                    label="Password"
                    name="password"
                    type="password"
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  />
                  <TextInput
                    label="Confirm Password"
                    name="password2"
                    type="password"
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  />
                  <SelectInput
                    label="Salutation"
                    name="salutationId"
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  >
                    <option value="">Select Salutation</option>
                    {salutations?.length ? (
                      salutations.map((s) => (
                        <option key={s.salutationId} value={s.salutationId}>
                          {s.salutationText}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No salutations available
                      </option>
                    )}
                  </SelectInput>
                  <SelectInput
                    label="Gender"
                    name="gender"
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  >
                    <option value="">Select Gender</option>
                    {genders?.length ? (
                      genders.map((g) => (
                        <option key={g.id} value={g.text}>
                          {g.text}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No genders available
                      </option>
                    )}
                  </SelectInput>
                  <TextInput
                    label="First Name"
                    name="firstName"
                    type="text"
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  />
                  <TextInput
                    label="Middle Name"
                    name="middleName"
                    type="text"
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  />
                  <TextInput
                    label="Last Name"
                    name="lastName"
                    type="text"
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  />
                  <div className="flex flex-col">
                    <TextInput
                      label="Mobile Number"
                      name="mobileNo"
                      type="text"
                      className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
                      labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                      errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                      onBlur={() => checkMobileExists(values.mobileNo)}
                      error={touched.mobileNo && errors.mobileNo}
                    />
                    {mobileError && (
                      <span className="text-red-600 font-['Semantic/Strong'] mt-1 text-sm">
                        {mobileError}
                      </span>
                    )}
                  </div>
                  <SelectInput
                    label="Role"
                    name="roleId"
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  >
                    <option value="">Select Role</option>
                    {roles?.length ? (
                      roles.map((r) => (
                        <option key={r.roleId} value={r.roleId}>
                          {r.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No roles available
                      </option>
                    )}
                  </SelectInput>
                  <SelectInput
                    label="Security Question"
                    name="securityQuestionId"
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  >
                    <option value="">Select Security Question</option>
                    {securityQuestions?.length ? (
                      securityQuestions.map((q) => (
                        <option key={q.securityQuestionId} value={q.securityQuestionId}>
                          {q.questionText}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No security questions available
                      </option>
                    )}
                  </SelectInput>
                  <TextInput
                    label="Recovery Hint"
                    name="recoveryHint"
                    type="text"
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  />
                  <DropdownWithCheckboxes
                    label="Target Languages"
                    options={targetLanguages.map((lang) => ({
                      languageId: lang.languageId,
                      name: lang.name,
                    }))}
                    values={values.targetLanguages}
                    onChange={setFieldValue}
                    name="targetLanguages"
                    touched={touched.targetLanguages}
                    errors={errors.targetLanguages}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700"
                    labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base mb-1"
                    errorClassName="text-red-600 font-['Semantic/Strong'] mt-1 text-sm"
                  />
                  <div className="col-span-full flex justify-center mt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting || loading}
                      className="bg-gradient-to-r from-[#ec7026] to-[#d85d1a] hover:from-[#d85d1a] hover:to-[#c04b0e] text-white font-['Semantic/Strong'] px-6 py-2 rounded-lg transition disabled:opacity-70"
                    >
                      {isSubmitting || loading ? "Registering..." : "Register"}
                    </button>
                  </div>
                  {(errors.salutationId ||
                    errors.gender ||
                    errors.roleId ||
                    errors.securityQuestionId) && (
                      <div className="col-span-full bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mt-4 font-['Semantic/Regular']">
                        <p>Please fill all required fields.</p>
                        <p className="mt-2 text-sm">Ensure all dropdowns and inputs are completed.</p>
                      </div>
                    )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterUserTableGAAdmin;